import * as zod from 'zod';

import { RoutineData } from './routine';

/**
 * Frozen copy of the routine a feedback row's chat session was about,
 * resolved SERVER-SIDE by feathers-bot's agent-feedback create hook from
 * the session's own `agentSessions.routineID` binding — never from
 * whatever page the client happened to be on. Captured at create time
 * because the live routine keeps being edited afterward — by triage
 * time the routines table no longer shows what the user was reacting to.
 *
 * `kind: 'routine'` carries the full canonical routine row; `kind:
 * 'deletedRoutine'` is the identity-only marker for the narrow race
 * where the session's bound routine disappears before the feedback
 * lands (the FK is ON DELETE SET NULL, so a long-deleted routine
 * usually manifests as a null binding — and a null snapshot — instead).
 */
export const AgentFeedbackRoutineSnapshot = zod.discriminatedUnion('kind', [
  zod.object({
    kind: zod.literal('routine'),
    routineID: zod.string(),
    name: zod.string().nullable(),
    capturedAt: zod.string().datetime(),
    routine: RoutineData,
  }),
  zod.object({
    kind: zod.literal('deletedRoutine'),
    routineID: zod.string(),
    capturedAt: zod.string().datetime(),
  }),
]);

export type AgentFeedbackRoutineSnapshot = zod.infer<
  typeof AgentFeedbackRoutineSnapshot
>;

/**
 * What kind of thing the operator is reporting. One vocabulary shared by
 * the chat UI, the Postgres row, and the Braintrust triage report, so a
 * count of "Wrong result" rows means the same thing in every place.
 */
export const FeedbackTag = zod.enum([
  'Worked well',
  'Wrong result',
  'Did not understand me',
  'Missing capability',
  'Trust and transparency',
  'UI issue',
  'Too slow',
  'Could not recover',
  'Suggestion',
]);

export type FeedbackTag = zod.infer<typeof FeedbackTag>;

export const AgentFeedbackData = zod.object({
  id: zod.string(),
  sessionID: zod.string(),
  messageID: zod.string(),
  sentiment: zod.enum(['up', 'down']),
  content: zod.string().nullable(),
  /** Zero or more tags picked in the reason modal. */
  tags: zod.array(FeedbackTag),
  /**
   * Text the operator selected inside the rated reply before rating it,
   * so the row says which sentence they were reacting to.
   */
  quote: zod.string().nullable(),
  routineSnapshot: AgentFeedbackRoutineSnapshot.nullable(),
  conversationSnapshotMessageIDs: zod.array(zod.string()),
  createdAt: zod.string(),
});

export type AgentFeedbackData = zod.infer<typeof AgentFeedbackData>;

/**
 * Client-suppliable fields only. `routineSnapshot` is deliberately
 * absent: the create hook resolves it authoritatively from the
 * session's routine binding, and `parse()` strips any client-sent
 * value before the hook runs.
 */
export const AgentFeedbackCreateInput = zod.object({
  sessionID: zod.string(),
  messageID: zod.string(),
  sentiment: zod.enum(['up', 'down']),
  content: zod.string().nullable().optional(),
  tags: zod.array(FeedbackTag).max(FeedbackTag.options.length).default([]),
  quote: zod.string().max(1000).nullable().optional(),
  conversationSnapshotMessageIDs: zod.array(zod.string()),
});

export type AgentFeedbackCreateInput = zod.infer<
  typeof AgentFeedbackCreateInput
>;
